import { AnchorButton, Menu, MenuItem } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Popover2 } from '@blueprintjs/popover2';
import classNames from 'classnames';
import * as React from 'react';
import { ItemSelector } from 'vet-bones/components';
import { SelectItem } from 'vet-bones/utils/select';

import {
  TEST_IDS,
  getProjectMappingAttributeFieldMenuTestId,
  getProjectMappingAttributeFieldSelectorTestId,
  getProjectMappingSelectItemTestId,
} from 'src/constants/testIds';
import { useSuggestionContext } from 'src/containers/ProjectMapping/hooks/suggestion';
import * as copy from 'src/templates/copy';

type ProjectClassMappingAttributeSubRowProps = {
  fieldItems: SelectItem[];
  index: number;
  noBorder: boolean;
  onAddRow: () => void;
  onRemoveRow?: () => void;
  onSelectField: (item: SelectItem | null) => void;
  selectedFieldItem: SelectItem | null;
  testId: string;
  highlighted: boolean;
};

export const ProjectClassMappingAttributeFieldRow: React.VFC<
  ProjectClassMappingAttributeSubRowProps
> = ({
  fieldItems,
  index,
  noBorder,
  onAddRow,
  onRemoveRow,
  onSelectField,
  selectedFieldItem,
  testId,
  highlighted,
}) => {
  const { isLoading } = useSuggestionContext();
  const gridCellClassNames = classNames('sd-project-mapping-grid-cell', {
    'sd-project-mapping-grid-cell--no-border': noBorder,
  });

  return (
    <>
      {index === 0 ? null : (
        <div
          className={classNames(
            gridCellClassNames,
            'sd-project-mapping-grid-cell--border-right'
          )}
        />
      )}

      <div
        className={classNames(
          gridCellClassNames,
          'sd-draft-highlight-candidate',
          { 'sd-draft-highlight-candidate--highlighted': highlighted }
        )}
        data-testid={getProjectMappingAttributeFieldSelectorTestId(
          testId,
          index
        )}
      >
        <ItemSelector.Single
          buttonText={selectedFieldItem?.text || ''}
          clearable
          clearTestId={TEST_IDS.selector.clear}
          disabled={isLoading}
          filterText={copy.components.defaults.filter}
          items={fieldItems}
          itemTestId={getProjectMappingSelectItemTestId}
          onSelect={onSelectField}
          selected={selectedFieldItem}
          testId={TEST_IDS.projectMapping.field.selector}
        />
      </div>
      <div
        className={classNames(
          gridCellClassNames,
          'sd-draft-highlight-candidate',
          { 'sd-draft-highlight-candidate--highlighted': highlighted }
        )}
      />
      <div
        className={classNames(
          gridCellClassNames,
          'sd-draft-highlight-candidate',
          { 'sd-draft-highlight-candidate--highlighted': highlighted }
        )}
      />

      <div
        className={classNames(
          gridCellClassNames,
          'sd-draft-highlight-candidate',
          { 'sd-draft-highlight-candidate--highlighted': highlighted }
        )}
        data-testid={getProjectMappingAttributeFieldMenuTestId(testId, index)}
      >
        <Popover2
          content={
            <Menu>
              <MenuItem
                data-testid={TEST_IDS.projectMapping.field.menu.duplicate}
                onClick={onAddRow}
                text={copy.components.projectMapping.menu.duplicateMapping}
              />
              {onRemoveRow ? (
                <MenuItem
                  data-testid={TEST_IDS.projectMapping.field.menu.remove}
                  onClick={onRemoveRow}
                  text={copy.components.projectMapping.menu.removeMapping}
                />
              ) : null}
            </Menu>
          }
          minimal
        >
          <AnchorButton
            data-testid={TEST_IDS.projectMapping.field.menu.button}
            disabled={isLoading}
            icon={IconNames.MORE}
            minimal
          />
        </Popover2>
      </div>
    </>
  );
};
